import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery, useQuery, queryOptions } from "@tanstack/react-query";
import { Clock, Zap, Target, FileText, CheckCircle2, PlayCircle, Lock } from "lucide-react";
import { getSimulationBySlug } from "@/lib/api/lxp-simulations.functions";
import { supabase } from "@/integrations/supabase/client";

const simQO = (slug: string) =>
  queryOptions({
    queryKey: ["lxp", "simulation", slug],
    queryFn: () => getSimulationBySlug({ data: { slug } }),
  });

export const Route = createFileRoute("/lexpractiq/simulations/$slug")({
  loader: async ({ context, params }) => {
    const data = await context.queryClient.ensureQueryData(simQO(params.slug));
    if (!data.simulation) throw notFound();
    return data;
  },
  head: ({ loaderData }) => {
    if (!loaderData?.simulation) return { meta: [{ title: "Simulation | SA LexPractiq" }, { name: "robots", content: "noindex" }] };
    const s = loaderData.simulation;
    return {
      meta: [
        { title: `${s.title} | SA LexPractiq` },
        { name: "description", content: s.summary },
        { property: "og:title", content: s.title },
        { property: "og:description", content: s.summary },
        { property: "og:type", content: "website" },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="lxp-container" style={{ padding: "5rem 1.25rem", textAlign: "center" }}>
      <h1>Simulation not found</h1>
      <Link to="/lexpractiq/simulations" className="lxp-btn" style={{ marginTop: "1rem" }}>Back to marketplace</Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="lxp-container" style={{ padding: "3rem 1.25rem" }}>
      <div className="lxp-card">
        <h2>Unable to load simulation</h2>
        <p className="lxp-muted">{error instanceof Error ? error.message : "Please try again."}</p>
      </div>
    </div>
  ),
  component: SimulationDetail,
});

type LxpTask = { key: string; title: string; brief: string; rubric?: string[] };

function SimulationDetail() {
  const { slug } = Route.useParams();
  const { data } = useSuspenseQuery(simQO(slug));
  const s = data.simulation!;
  const tasks = (s.tasks || []) as LxpTask[];
  const outcomes = (s.learning_outcomes || []) as string[];
  const competencies = (s.competencies || []) as Array<{ label: string }>;
  const docs = (s.document_pack || []) as Array<{ title: string; body: string }>;
  return (
    <div className="lxp-container" style={{ padding: "3rem 1.25rem", display: "grid", gap: "2rem", gridTemplateColumns: "minmax(0,2fr) 320px" }}>
      <div>
        <span className="lxp-badge">{s.practice_area} · {s.difficulty}</span>
        <h1 style={{ fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)", margin: "0.5rem 0 0.75rem", letterSpacing: "-0.02em" }}>{s.title}</h1>
        <p className="lxp-muted" style={{ fontSize: "1.05rem", lineHeight: 1.6, margin: "0 0 1.5rem" }}>{s.summary}</p>

        <div className="lxp-card" style={{ marginBottom: "1.5rem" }}>
          <h2 style={{ fontSize: "1.05rem", margin: "0 0 0.5rem" }}>Your role</h2>
          <p className="lxp-muted" style={{ margin: "0 0 1rem", fontSize: "0.9rem" }}>{s.role_played}</p>
          <h3 style={{ fontSize: "0.95rem", margin: "1rem 0 0.5rem" }}>Scenario</h3>
          <p style={{ margin: 0, lineHeight: 1.65, fontSize: "0.9rem", whiteSpace: "pre-wrap" }}>{s.scenario_intro}</p>
        </div>

        <h2 style={{ fontSize: "1.2rem", margin: "1.5rem 0 0.75rem" }}>
          <Target size={18} style={{ verticalAlign: "-2px", marginRight: 6, color: "var(--lxp-accent)" }} />
          Learning outcomes
        </h2>
        <ul style={{ paddingLeft: "1.25rem", lineHeight: 1.75, fontSize: "0.9rem" }}>
          {outcomes.map((o, i) => <li key={i} className="lxp-muted">{o}</li>)}
        </ul>

        <h2 style={{ fontSize: "1.2rem", margin: "1.5rem 0 0.75rem" }}>
          <FileText size={18} style={{ verticalAlign: "-2px", marginRight: 6, color: "var(--lxp-accent)" }} />
          Tasks you'll complete
        </h2>
        <div style={{ display: "grid", gap: "0.75rem" }}>
          {tasks.map((t, i) => (
            <div key={t.key} className="lxp-card">
              <div style={{ display: "flex", alignItems: "start", gap: "0.75rem" }}>
                <span
                  style={{
                    minWidth: 28, height: 28, borderRadius: 8, background: "var(--lxp-accent-glow)", color: "var(--lxp-accent)",
                    display: "grid", placeItems: "center", fontWeight: 700, fontSize: "0.8rem",
                  }}
                >{i + 1}</span>
                <div>
                  <h3 style={{ margin: "0 0 0.3rem", fontSize: "1rem" }}>{t.title}</h3>
                  <p className="lxp-muted" style={{ margin: 0, fontSize: "0.85rem", lineHeight: 1.55 }}>{t.brief}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <h2 style={{ fontSize: "1.2rem", margin: "1.5rem 0 0.75rem" }}>Document pack</h2>
        <p className="lxp-muted" style={{ fontSize: "0.85rem", margin: "0 0 0.75rem" }}>
          You'll receive {docs.length} case documents when the simulation starts.
        </p>
      </div>

      <aside>
        <div className="lxp-card" style={{ position: "sticky", top: "5rem" }}>
          <StartOrSignIn slug={s.slug} />
          <p className="lxp-muted" style={{ fontSize: "0.75rem", textAlign: "center", margin: "0 0 1rem" }}>
            Only signed-in participants can start this simulation. Free to join.
          </p>
          <div style={{ display: "grid", gap: "0.5rem", fontSize: "0.85rem" }}>
            <Row icon={<Clock size={14} />} label="Duration" value={`~${Math.round(s.duration_min / 60)}h`} />
            <Row icon={<Zap size={14} />} label="XP reward" value={`${s.xp_reward} XP`} />
            <Row icon={<CheckCircle2 size={14} />} label="Certificate" value="Yes, on completion" />
          </div>
          <div className="lxp-divider" style={{ margin: "1.25rem 0" }} />
          <h4 style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--lxp-text-muted)", margin: "0 0 0.6rem" }}>
            Competencies developed
          </h4>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
            {competencies.map((c, i) => <span key={i} className="lxp-badge">{c.label}</span>)}
          </div>
        </div>
      </aside>

      <style>{`@media (max-width: 900px){.lxp-container[style*="grid-template-columns"]{grid-template-columns:1fr !important;}}`}</style>
    </div>
  );
}

function Row({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <span className="lxp-muted" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
        {icon} {label}
      </span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  );
}

function StartOrSignIn({ slug }: { slug: string }) {
  const { data: session, isLoading } = useQuery({
    queryKey: ["auth", "session"],
    queryFn: async () => (await supabase.auth.getSession()).data.session,
    staleTime: 30_000,
  });
  if (isLoading) {
    return (
      <div className="lxp-btn" style={{ width: "100%", marginBottom: "1rem", opacity: 0.6, pointerEvents: "none" }}>
        Loading…
      </div>
    );
  }
  if (!session) {
    return (
      <Link
        to="/auth"
        search={{ portal: "lxp", redirect: `/lexpractiq/simulations/${slug}/run` } as any}
        className="lxp-btn"
        style={{ width: "100%", marginBottom: "1rem" }}
      >
        <Lock size={16} /> Sign in to participate
      </Link>
    );
  }
  return (
    <Link
      to="/lexpractiq/simulations/$slug/run"
      params={{ slug }}
      className="lxp-btn"
      style={{ width: "100%", marginBottom: "1rem" }}
    >
      <PlayCircle size={16} /> Start simulation
    </Link>
  );
}
